/**
 * ReservePay Payment Types
 * Created: 2026-06-13
 *
 * Types for ReservePay hosted checkout flow (PromptPay-first).
 * Used for both credit top-up and package purchase via ReservePay.
 */

// ============================================================================
// ReservePay Checkout Data (from ReservePay API)
// ============================================================================

export interface ReservePayCheckoutData {
  /** ReservePay hosted checkout identifier */
  checkout_id?: string
  /** URL to redirect user to for payment */
  checkout_url?: string
  status?: string
  amount?: number
  currency?: string
  [key: string]: unknown
}

// ============================================================================
// API Response Types
// ============================================================================

export interface ReservePayTransaction {
  id: string
  reference_number: string
  transaction_type: string
  status: string
  amount: number
  final_amount: number
  credit_amount?: number
  expires_at: string
  created_at?: string
}

export interface ReservePayCreditCheckoutResponse {
  success: boolean
  data: {
    transaction: ReservePayTransaction
    provider: 'reservepay'
    checkout: ReservePayCheckoutData
  }
  message?: string
}

export interface ReservePayPackageCheckoutResponse {
  success: boolean
  data: {
    transaction: ReservePayTransaction
    provider: 'reservepay'
    checkout: ReservePayCheckoutData
    activationChoice: 'immediate' | 'queued'
  }
  message?: string
}

// ============================================================================
// Payment Status (shared with manual payment flow)
// ============================================================================

export type ReservePayTransactionStatus =
  | 'created'
  | 'pending'
  | 'completed'
  | 'failed'
  | 'expired'
  | 'cancelled'
  | 'rejected'

export interface PaymentStatusResponse {
  success: boolean
  data: {
    transaction: {
      id: string
      reference_number: string
      status: ReservePayTransactionStatus
      amount: number
      final_amount: number
      credit_amount?: number
      transaction_type?: string
      package_name?: string
      payment_method?: string
      completed_at?: string
      expires_at?: string
    }
    isCompleted: boolean
    isFailed: boolean
  }
}

// ============================================================================
// sessionStorage key for pending transaction
// ============================================================================

export const RESERVEPAY_PENDING_TXN_KEY = 'reservepay_pending_txid'
export const RESERVEPAY_PENDING_TYPE_KEY = 'reservepay_pending_type' // 'credit' | 'package'

// ============================================================================
// Custom UI Flow — initiate-reservepay-flow response
// ============================================================================

export interface ReservePayInitiateCreditFlowResponse {
  success: boolean
  data: {
    transaction: ReservePayTransaction
    provider: 'reservepay'
    /** ReservePay payment_id returned by initiate-payment-flow */
    payment_id: string
  }
  message?: string
}

export interface ReservePayInitiatePackageFlowResponse {
  success: boolean
  data: {
    transaction: ReservePayTransaction
    provider: 'reservepay'
    payment_id: string
    activationChoice: 'immediate' | 'queued'
  }
  message?: string
}

